import React, { useMemo } from 'react';

import { Box, Center, HStack, Stack } from '@chakra-ui/react';
import { customTheme } from 'theme';

import { BarChart } from 'components/Visualization';
import { DashboardWidget } from 'components/Visualization/CDashboard/Widget/DashboardWidget';
import {
  customizedXAxisTick,
  rotatedXAxisTick,
  sortedLineChartTooltip,
} from 'components/Visualization/Charts/utils';

import { CloudEventsSummaryChartProps } from '../../../types';

import {
  formatCloudEventsSummaryTime,
  getCloudEventsSummaryXAxisInterval,
} from './utils';

const actionColors = [
  customTheme.colors.blue['100'],
  customTheme.colors.yellow['100'],
  customTheme.colors.red['100'],
  customTheme.colors.red['300'],
  customTheme.colors.purple['100'],
  customTheme.colors.green['100'],
  customTheme.colors.skyBlue['100'],
  customTheme.colors.indigo['50'],
];

export const APICallsByActionTypes = ({
  summary,
  isHourly,
  summaryPeriod,
}: CloudEventsSummaryChartProps) => {
  const { actionTypes, chartData, colorMap } = useMemo(() => {
    const types = new Set<string>();

    summary.data?.by_action_type?.forEach(point => {
      point.actions?.forEach(action => {
        if (action.action) types.add(String(action.action));
      });
    });

    const actionTypes = Array.from(types);
    const colorMap = actionTypes.reduce<Record<string, string>>(
      (colors, action, index) => {
        colors[action] = actionColors[index % actionColors.length];
        return colors;
      },
      {},
    );
    const chartData =
      summary.data?.by_action_type?.map(point => {
        const row: Record<string, string | number> = {
          Date: formatCloudEventsSummaryTime(point),
        };

        actionTypes.forEach(action => {
          row[action] = 0;
        });
        point.actions?.forEach(action => {
          if (!action.action) return;
          const actionName = String(action.action);
          row[actionName] =
            Number(row[actionName] ?? 0) + Number(action.count ?? 0);
        });

        return row;
      }) ?? [];

    return { actionTypes, chartData, colorMap };
  }, [summary.data?.by_action_type]);

  return (
    <DashboardWidget
      label="API Calls By Action Type"
      subHeader={isHourly ? undefined : summaryPeriod ?? 'Last 7 days'}
      isLoading={summary.isLoading}
      noData={!chartData.length || !actionTypes.length}
      paddingX={0}
      paddingBottom={1}
      content={
        <Stack w="full" h="full" spacing={2}>
          <Center>
            <HStack spacing={3} flexWrap="wrap">
              {actionTypes.map(action => (
                <HStack key={action} spacing={1.5} fontSize="sm">
                  <Box boxSize={1.5} bg={colorMap[action]} />
                  <Box color={customTheme.colors.gray['300']}>{action}</Box>
                </HStack>
              ))}
            </HStack>
          </Center>
          <Box flex={1} minH={0}>
            <BarChart
              data={chartData}
              xField="Date"
              yField={actionTypes}
              legend={false}
              stacked
              colorMap={colorMap}
              customTooltipContent={sortedLineChartTooltip}
              styles={{
                barChart: {
                  margin: { bottom: 5, left: 0, top: 5 },
                },
                bar: { barSize: 15 },
                xAxis: {
                  axisLine: true,
                  tickLine: true,
                  tick: isHourly ? customizedXAxisTick : rotatedXAxisTick,
                  height: isHourly ? 30 : 55,
                  interval: getCloudEventsSummaryXAxisInterval(
                    isHourly,
                    chartData.length,
                  ),
                },
                yAxis: {
                  axisLine: false,
                  tickLine: false,
                  width: 65,
                  tickCount: 5,
                  interval: 0,
                },
              }}
            />
          </Box>
        </Stack>
      }
    />
  );
};
